import {
  BookingAdmin,
  Booking,
  BookingStatus,
  BookingStatusEnum,
} from '@/types';
import api from '../client/axiosInstance';
import axios, { AxiosError } from 'axios';
import { withErrorCode } from '@/helpers/subscriptionError';

interface UpdateProps {
  dateTime: Date;
  duration: number;
  status: BookingStatusEnum;
  bookingLink?: string;
}

interface BookingQuery {
  status?: BookingStatusEnum;
  limit?: number;
}

export interface BookingResponse {
  sessions: Booking[];
  total: Record<Exclude<BookingStatusEnum, BookingStatus.DECLINED>, number>;
}

interface BookACallProps {
  consultantId: string;
  slot: {
    start: string;
    end: string;
  };
  timeZone: string;
  // TCX-821: a per-attempt key so a retried booking (lost response, network
  // blip) replays the original result server-side instead of creating a
  // duplicate. Reused across retries of the same attempt by the caller.
  idempotencyKey?: string;
}

/**
 * TCX-900: the server's error code, if this failure actually came back from our
 * API. Every write in this file is subscription-gated, so each one can answer
 * `SUBSCRIPTION_UNVERIFIABLE` during a Stripe outage — and the flattening below
 * (callers compare `error.message`) would otherwise drop the only field that
 * tells a retryable outage apart from a rejected action.
 */
const serverErrorCode = (error: unknown): string | undefined =>
  axios.isAxiosError(error) ? error.response?.data?.error : undefined;

const handleError = (error: unknown, message: string): never => {
  if (axios.isAxiosError(error)) {
    // The shared flattening seam for the availability / reschedule /
    // late-cancel / review writes — so the code has to survive it, not just
    // the prose.
    throw withErrorCode(
      new Error(`${message}: ${error.response?.data?.message || error.message}`),
      serverErrorCode(error),
    );
  } else {
    throw new Error(`${message}: Unknown error occurred`);
  }
};

export const bookACall = async ({
  consultantId,
  slot,
  timeZone,
  idempotencyKey,
}: BookACallProps) => {
  try {
    const response = await api.post(
      `/session`,
      {
        consultantId,
        start: slot.start,
        end: slot.end,
        timeZone,
      },
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined,
    );

    return response.data;
  } catch (error: any) {
    if (error instanceof AxiosError) {
      // TCX-900: keep the server's error CODE on the flattened error. Callers
      // still read `.message`, but a subscription that merely could not be
      // verified has to stay distinguishable from a real booking failure.
      throw withErrorCode(
        new Error(
          error.response?.data?.message ||
            'Something went wrong. Please try again.',
        ),
        error.response?.data?.error,
      );
    } else {
      throw new Error(error.message);
    }
  }
};

export const getBookings = async (data?: BookingQuery) => {
  try {
    const params: Record<string, any> = {};

    if (data?.status) {
      params.status = data.status;
    }

    if (data?.limit) {
      params.limit = data.limit;
    }

    const response = await api.get('/session', { params });
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const getAvailability = async (consultantId: string) => {
  try {
    const response = await api.get(
      `/booking/cronofy/availabilityRule/${consultantId}`,
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export interface WeeklyPeriod {
  day: string;
  start_time: string; // 'HH:mm'
  end_time: string; // 'HH:mm'
}

export interface ConsultantAvailability {
  calendarConnected: boolean;
  timezone: string; // tz the editor operates in (consultant's own)
  ruleTimezone?: string; // tz the stored periods are expressed in
  weekly_periods: WeeklyPeriod[];
}

// TCX-703: the authenticated consultant's own editable weekly availability.
export const getMyAvailability = async (): Promise<ConsultantAvailability> => {
  try {
    const response = await api.get('/availability/me');
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to load availability');
  }
};

export const updateAvailability = async (
  weekly_periods: WeeklyPeriod[],
): Promise<ConsultantAvailability> => {
  try {
    const response = await api.put('/availability/me', { weekly_periods });
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to update availability');
  }
};

export const getLatestCompletedSessions = async () => {
  try {
    const response = await api.get('/session/latest-completed');
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const getConsultantFeedbackRequest = async () => {
  try {
    const response = await api.get('/session/consultant-feedback-request');
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const skipConsultantFeedbackRequest = async (sessionId: string) => {
  try {
    const response = await api.post(
      `/session/${sessionId}/skip-consultant-feedback-request`,
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      // TCX-900: carry the server error code through the flattening, same as
      // `handleError` does for its writes.
      throw withErrorCode(new Error(error.message), serverErrorCode(error));
    }
  }
};

export const skipFranchiseeFeedbackRequest = async (sessionId: string) => {
  try {
    const response = await api.post(
      `/session/${sessionId}/skip-franchisee-feedback-request`,
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      // TCX-900: carry the server error code through the flattening, same as
      // `handleError` does for its writes.
      throw withErrorCode(new Error(error.message), serverErrorCode(error));
    }
  }
};

export const getAdminBookings = async ({
  consultantId,
  franchiseId,
  search,
  startDate,
  endDate,
  page,
  limit,
}: {
  consultantId?: string;
  franchiseId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  sessions: BookingAdmin[];
}> => {
  try {
    if (consultantId) {
      const response = await api.get(
        `admin/sessions?consultantId=${consultantId}`,
        {
          params: {
            search,
            startDate,
            endDate,
            page,
            limit,
          },
        },
      );
      return response.data;
    } else if (franchiseId) {
      const response = await api.get(
        `admin/sessions?franchiseId=${franchiseId}`,
        {
          params: {
            search,
            startDate,
            endDate,
            page,
            limit,
          },
        },
      );
      return response.data;
    } else {
      const response = await api.get('admin/sessions', {
        params: {
          search,
          startDate,
          endDate,
          page,
          limit,
        },
      });
      return response.data;
    }
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    return {
      total: 0,
      page: 0,
      limit: 0,
      sessions: [],
    };
  }
};

export const getAdminBookingById = async (id: string) => {
  try {
    const response = await api.get(`admin/sessions/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const getBookingById = async (id: string) => {
  try {
    const response = await api.get(`/session/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const updateBooking = async (id: string, data: Partial<UpdateProps>) => {
  try {
    const response = await api.patch(`/session/${id}`, data);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      // TCX-900: carry the server error code through the flattening, same as
      // `handleError` does for its writes.
      throw withErrorCode(new Error(error.message), serverErrorCode(error));
    }
  }
};

export const deleteBooking = async (id: string) => {
  try {
    const response = await api.delete(`/session/${id}`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      // TCX-900: carry the server error code through the flattening, same as
      // `handleError` does for its writes.
      throw withErrorCode(new Error(error.message), serverErrorCode(error));
    }
  }
};

export const requestReschedule = async (
  id: string,
  slot: { start: string; end: string; timeZone: string },
) => {
  try {
    const response = await api.post(`/session/${id}/reschedule-request`, slot);
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to request reschedule');
  }
};

export const approveReschedule = async (id: string) => {
  try {
    const response = await api.post(`/session/${id}/reschedule/approve`);
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to approve reschedule');
  }
};

export const denyReschedule = async (id: string) => {
  try {
    const response = await api.post(`/session/${id}/reschedule/deny`);
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to keep fee');
  }
};

export const rejectReschedule = async (id: string) => {
  try {
    const response = await api.post(`/session/${id}/reschedule/reject`);
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to reject reschedule');
  }
};

export const lateCancelBooking = async (id: string) => {
  try {
    const response = await api.post(`/session/${id}/late-cancel`);
    return response.data;
  } catch (error) {
    return handleError(error, 'Failed to cancel meeting');
  }
};

export const reviewBooking = async (
  id: string,
  data: { rating: number; comment?: string },
) => {
  try {
    const response = await api.post(`/session/${id}/review`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Error reviewing booking');
  }
};

export const reviewConsultantSession = async (
  id: string,
  data: {
    rating: number;
    comment?: string;
    ratings: { questionId: string; rating: number }[];
    franchise?: string;
  },
) => {
  try {
    const response = await api.post(`/session/${id}/review-consultant`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'Error reviewing consultant session');
  }
};

export const getConsultantReviewForSession = async (id: string) => {
  try {
    const response = await api.get(`/session/${id}/review-consultant`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
  }
};

export const reviewBookingRequest = async (id: string) => {
  try {
    const response = await api.post(`/session/${id}/review-request`);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      // TCX-900: carry the server error code through the flattening, same as
      // `handleError` does for its writes.
      throw withErrorCode(new Error(error.message), serverErrorCode(error));
    }
  }
};
